import React, { useEffect, useState } from 'react';
import { css, cx } from 'emotion';
import ArrowRightIcon from '@frontend/static/icons/arrow-right.svg';
import { brandText, brandAlt, neutral, } from '@guardian/src-foundations/palette';
import { textSans, headline } from '@guardian/src-foundations/typography';
import { from, until } from '@guardian/src-foundations/mq';
import { shouldHideSupportMessaging } from '@root/src/web/lib/contributions';
import { setAutomat } from '@root/src/web/lib/setAutomat';
import { getCookie } from '@root/src/web/browser/cookie';
import { remoteRrHeaderLinksTestName } from '@root/src/web/experiments/tests/remoteRrHeaderLinksTest';
import { useHasBeenSeen } from '@root/src/web/lib/useHasBeenSeen';
import { addTrackingCodesToUrl } from '@root/src/web/lib/acquisitions';
import { sendOphanComponentEvent, } from '@root/src/web/browser/ophan/ophan';
const headerStyles = css `
	${until.mobileLandscape} {
		padding-left: 10px;
	}
	${until.tablet} {
		padding-top: 33px;
	}
	${from.mobileLandscape} {
		padding-left: 20px;
	}
	${until.desktop} {
		max-width: 310px;
	}
`;
const messageStyles = (isThankYouMessage) => css `
	color: ${brandAlt[400]};
	${headline.xxsmall({ fontWeight: 'bold' })};
	padding-top: 3px;
	margin-bottom: 3px;

	${from.desktop} {
		${headline.xsmall({ fontWeight: 'bold' })}
	}

	${from.leftCol} {
		${isThankYouMessage
    ? headline.small({ fontWeight: 'bold' })
    : headline.medium({ fontWeight: 'bold' })}
	}
`;
const linkStyles = css `
	background: ${brandAlt[400]};
	border-radius: 16px;
	box-sizing: border-box;
	color: ${neutral[7]};
	float: left;
	${textSans.small()};
	font-weight: 700;
	height: 32px;
	text-decoration: none;
	padding: 6px 12px 0 12px;
	line-height: 18px;
	position: relative;
	margin-right: 10px;
	margin-bottom: 6px;

	${from.mobileMedium} {
		padding-right: 34px;
	}

	svg {
		fill: currentColor;
		position: absolute;
		right: 3px;
		top: 50%;
		height: 32px;
		width: 32px;
		transform: translate(0, -50%);
		transition: transform 0.3s ease-in-out;

		${until.mobileMedium} {
			display: none;
		}
	}

	:hover svg {
		transform: translate(3px, -50%);
	}
`;
const hidden = css `
	display: none;
`;
const hiddenUntilTablet = css `
	${until.tablet} {
		display: none;
	}
`;
const hiddenFromTablet = css `
	${from.tablet} {
		display: none;
	}
`;
const subMessageStyles = css `
	color: ${brandText.primary};
	${textSans.medium()};
	margin: 5px 0;
`;
const ReaderRevenueLinksRemote = ({ edition, countryCode, pageViewId, contributionsServiceUrl, ophanRecord, }) => {
    const [supportHeaderResponse, setSupportHeaderResponse,] = useState(null);
    const [SupportHeader, setSupportHeader,] = useState(null);
    const [hasBeenSeen, setNode] = useHasBeenSeen({
        threshold: 0,
        debounce: true,
    });
    useEffect(() => {
        if (hasBeenSeen && supportHeaderResponse) {
            sendOphanComponentEvent('VIEW', supportHeaderResponse.meta, ophanRecord);
        }
    }, [hasBeenSeen, supportHeaderResponse, ophanRecord]);
    useEffect(() => {
        setAutomat();
        const requestData = {
            tracking: {
                ophanPageId: pageViewId,
                platformId: 'GUARDIAN_WEB',
                referrerUrl: window.location.origin + window.location.pathname,
                clientName: 'dcr',
            },
            targeting: {
                showSupportMessaging: !shouldHideSupportMessaging(),
                edition,
                countryCode,
                modulesVersion: 'v1',
                mvtId: Number(getCookie('GU_mvt_id')),
            },
        };
        fetch(`${contributionsServiceUrl}/header`, {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData),
        })
            .then((result) => result.json())
            .then((response) => {
            if (!response.data) {
                return null;
            }
            setSupportHeaderResponse(response.data);
            const { module, meta } = response.data;
            return window
                .guardianPolyfilledImport(module.url)
                .then((headerModule) => {
                setSupportHeader(() => headerModule.Header);
                sendOphanComponentEvent('INSERT', meta, ophanRecord);
            });
        })
            .catch((error) => {
            const msg = `Error importing RR header links: ${error}`;
            // eslint-disable-next-line no-console
            console.log(msg);
            window.guardian.modules.sentry.reportError(new Error(msg), 'rr-header-links');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (SupportHeader && supportHeaderResponse) {
        return (React.createElement("div", { ref: setNode, className: headerStyles },
            React.createElement(SupportHeader, Object.assign({}, supportHeaderResponse.module.props))));
    }
    return null;
};
export const ReaderRevenueLinksNative = ({ edition, dataLinkNamePrefix, inHeader, urls, ophanRecord, pageViewId, }) => {
    const hideSupportMessaging = shouldHideSupportMessaging();
    // Only the header component is in the AB test
    const testName = inHeader ? remoteRrHeaderLinksTestName : 'RRFooterLinks';
    const campaignCode = `${testName}_control`;
    const tracking = {
        abTestName: testName,
        abTestVariant: 'control',
        componentType: inHeader ? 'ACQUISITIONS_HEADER' : 'ACQUISITIONS_FOOTER',
        campaignCode,
    };
    const [hasBeenSeen, setNode] = useHasBeenSeen({
        threshold: 0,
        debounce: true,
    });
    useEffect(() => {
        if (!hideSupportMessaging && inHeader) {
            sendOphanComponentEvent('INSERT', tracking, ophanRecord);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (hasBeenSeen && inHeader) {
            sendOphanComponentEvent('VIEW', tracking, ophanRecord);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasBeenSeen]);
    const getUrl = (rrType) => {
        if (inHeader) {
            return addTrackingCodesToUrl({
                base: `https://support.theguardian.com/${rrType}`,
                componentType: 'ACQUISITIONS_HEADER',
                componentId: campaignCode,
                campaignCode,
                abTest: {
                    name: remoteRrHeaderLinksTestName,
                    variant: 'control',
                },
                pageViewId,
                referrerUrl: window.location.origin + window.location.pathname,
            });
        }
        return urls[rrType];
    };
    if (hideSupportMessaging) {
        return (React.createElement("div", { className: cx(inHeader && headerStyles) },
            React.createElement("div", { className: cx({
                    [hiddenUntilTablet]: inHeader,
                }) },
                React.createElement("div", { className: messageStyles(true) }, " Thank you "),
                React.createElement("div", { className: subMessageStyles }, "Your support powers our independent journalism"))));
    }
    const ContributeButton = () => (React.createElement("a", { className: linkStyles, href: getUrl('contribute'), "data-link-name": `${dataLinkNamePrefix}contribute-cta` },
        "Contribute ",
        React.createElement(ArrowRightIcon, null)));
    const SubscribeButton = () => (React.createElement("a", { className: linkStyles, href: getUrl('subscribe'), "data-link-name": `${dataLinkNamePrefix}subscribe-cta` },
        "Subscribe ",
        React.createElement(ArrowRightIcon, null)));
    const PrimaryButton = edition === 'UK' ? SubscribeButton : ContributeButton;
    const SecondaryButton = edition === 'UK' ? ContributeButton : SubscribeButton;
    return (React.createElement("div", { ref: setNode, className: cx(inHeader && headerStyles) },
        React.createElement("div", { className: cx({
                [hiddenUntilTablet]: inHeader,
            }) },
            React.createElement("div", { className: messageStyles(false) },
                React.createElement("span", null, "Support the\u00A0Guardian")),
            React.createElement("div", { className: subMessageStyles },
                React.createElement("div", null, "Available for everyone, funded by readers")),
            React.createElement(PrimaryButton, null),
            React.createElement(SecondaryButton, null)),
        React.createElement("div", { className: cx({
                [hiddenFromTablet]: inHeader,
                [hidden]: !inHeader,
            }) },
            React.createElement(PrimaryButton, null))));
};
export const ReaderRevenueLinks = ({ edition, countryCode, dataLinkNamePrefix, inHeader, inRemoteModuleTest, urls, contributionsServiceUrl, ophanRecord, pageViewId = '', }) => {
    if (inHeader && inRemoteModuleTest) {
        return (React.createElement(ReaderRevenueLinksRemote, { edition: edition, countryCode: countryCode, pageViewId: pageViewId, contributionsServiceUrl: contributionsServiceUrl, ophanRecord: ophanRecord }));
    }
    return (React.createElement(ReaderRevenueLinksNative, { edition: edition, countryCode: countryCode, dataLinkNamePrefix: dataLinkNamePrefix, inHeader: inHeader, inRemoteModuleTest: inRemoteModuleTest, urls: urls, contributionsServiceUrl: contributionsServiceUrl, ophanRecord: ophanRecord, pageViewId: pageViewId }));
};
